import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import enlargeEnableIcon from "../../../assets/ecg-graph/enlarge_enable.svg";
import ecgMetaModalIcon from "../../../assets/ecg-graph/ecg_meta_modal.svg";
import {
  EcgDetailsActionType,
  useEcgDetailsContext,
} from "../../../context/EcgDetailsContext";
import useAlert from "../../../hooks/useAlert";
import useResponsive from "../../../hooks/useResponsive";
import ecgsService from "../../../services/ecgs.service";
import logger from "../../../utils/logger";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import { Button } from "@shltelemedicine/shl-ui-lib";
import DateTime from "../../Shared/DateTime/DateTime";
import EcgGraphModal from "../EcgGraphModal/EcgGraphModal";
import CompareEcgs from "./CompareEcgs/CompareEcgs";
import styles from "./EcgDetailsTopBar.module.css";
import Zoom from "./Zoom/Zoom";
import useCancelRequests from "../../../hooks/useCancelRequests";
import { useAuthContext } from "../../../context/AuthContext";
import Role from "../../../models/role";

type EcgDetailsTopBarProps = {
  zoomLevel?: number;
  minZoom?: number;
  maxZoom?: number;
  showZoomInfo?: boolean;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onShowEcgMeta?: () => void;
};

const FirstEcgButton = () => (
  <Button
    className={`bg-mx-2 ${styles.firstEcgButton} `}
    bgColor="transparent linear-gradient(180deg, #8FA7FF 0%, #8FA7FF 100%) 0% 0% no-repeat padding-box"
  >
    <FormattedMessage id="GLOBAL.FIRSTECG" defaultMessage="First ECG" />
  </Button>
);

const EcgDetailsTopBar = ({
  zoomLevel,
  minZoom,
  maxZoom,
  showZoomInfo,
  onShowEcgMeta,
  onZoomIn,
  onZoomOut,
}: EcgDetailsTopBarProps) => {
  const {
    state: { loading, compareEcg, selectedEcg, patientId },
    dispatch,
  } = useEcgDetailsContext();
  const {
    state: { user },
  } = useAuthContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [collapseZoomInfo, setCollapseZoomInfo] = useState(false);
  const [comparableEcgs, setComparableEcgs] = useState<
    { id: string; date: string; hasSymptoms: boolean; isUnreadable: boolean }[]
  >([]);
  const [displayFirstEcg, setDisplayFirstEcg] = useState(false);
  const [compareEcgsDisabled, setCompareEcgsDisabled] = useState(true);

  useEffect(() => {
    setCompareEcgsDisabled(true);
  }, [selectedEcg?.id]);

  useEffect(() => {
    setDisplayFirstEcg(false);
  }, [selectedEcg?.id]);

  const { isMobile, isTabletPortrait } = useResponsive();

  const {
    getToken: getCancelToken,
    isCanceled,
    disposeToken,
  } = useCancelRequests(EcgDetailsTopBar.name);

  useEffect(() => {
    (async () => {
      if (!patientId || !selectedEcg?.id) return;

      const { id: cancelTokenId, cancelToken } = getCancelToken();

      const data = await ecgsService.getPatientComparableEcgs({
        patientId,
        filterIds: [selectedEcg.id],
        cancelToken,
      });

      if (isCanceled(cancelTokenId)) {
        return;
      }

      disposeToken(cancelTokenId);

      setComparableEcgs(data ?? []);
      setDisplayFirstEcg(
        selectedEcg && !(data ?? []).length && user?.role === Role.Reviewer
      );
      setCompareEcgsDisabled(false);
    })();
  }, [
    patientId,
    selectedEcg?.id,
    disposeToken,
    getCancelToken,
    isCanceled,
    user?.role,
  ]);

  const compareEcgChangeHandler = async (compareId: string) => {
    const { id: cancelTokenId, cancelToken } = getCancelToken();

    try {
      dispatch({
        type: EcgDetailsActionType.SET_LOADING,
        payload: true,
      });

      dispatch({
        type: EcgDetailsActionType.SET_SELECTED_COMPARE_ECG,
        payload: null,
      });

      if (!compareId || !selectedEcg) {
        logger.error(`[${compareEcgChangeHandler.name} Error] Invalid inputs`);
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.LOADING_COMPARE_ECG",
          "An error occurred while loading previous ECG data.<br></br>Please try again.",
          {
            br: () => <br></br>,
          }
        );
        showErrorAlert(message, support);
        return;
      }

      const ecgToCompare = comparableEcgs?.find((ecg) => ecg.id === compareId);
      if (!ecgToCompare) {
        logger.error(
          `[${compareEcgChangeHandler.name} Error] Unable to find ecg to compare (Id: ${compareId})`
        );
        return;
      }

      const { success, data, errorCode } = await ecgsService.getEcgDetails({
        id: selectedEcg.id,
        compareId: ecgToCompare.id,
        cancelToken,
      });

      if (isCanceled(cancelTokenId)) {
        return;
      }

      if (!success || !data) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.LOADING_COMPARE_ECG",
          "An error occurred while loading previous ECG data.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      const { ecgDetails, compareEcgDetails } = data;

      dispatch({
        type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
        payload: { ...selectedEcg, graph: ecgDetails.graph },
      });

      if (compareEcgDetails) {
        dispatch({
          type: EcgDetailsActionType.SET_SELECTED_COMPARE_ECG,
          payload: {
            ...compareEcgDetails,
            date: ecgToCompare.date,
          },
        });
      }
    } finally {
      dispatch({
        type: EcgDetailsActionType.SET_LOADING,
        payload: false,
      });
      disposeToken(cancelTokenId);
    }
  };

  const handleShowEcgModal = () => setGraphModalOpen(true);
  const handleCloseEcgModal = () => setGraphModalOpen(false);
  const toggleCollapseZoomInfo = () => setCollapseZoomInfo((prev) => !prev);

  return (
    <>
      <header
        className={`${styles.header} d-flex align-items-center justify-content-between p-2 app-shadow`}
      >
        <p className={`${styles.title} mb-0`}>
          <strong>
            {selectedEcg ? (
              <>
                <DateTime showTime>{selectedEcg?.date}</DateTime>
              </>
            ) : (
              !loading && (
                <span>
                  <FormattedMessage
                    id="ECG_DETAILS.SELECT_ECG"
                    defaultMessage="Select ECG to view graphs"
                  />
                </span>
              )
            )}
          </strong>
        </p>
        <div className="d-flex align-items-center">
          <span className="mx-sm"></span>

          {showZoomInfo && !isTabletPortrait && (
            <div
              className={`${styles.zoomInfo} font-sm d-sm-flex d-none align-items-start p-1 rounded-sm`}
            >
              {!collapseZoomInfo && (
                <p
                  style={{ lineHeight: "1", maxWidth: "8rem" }}
                  className="p-0 m-0"
                >
                  {zoomLevel! > minZoom! ? (
                    <FormattedMessage
                      id="ECG_DETAILS.ZOOM_INFO.DRAG"
                      defaultMessage="Click and drag to move the image"
                    />
                  ) : (
                    <FormattedMessage
                      id="ECG_DETAILS.ZOOM_INFO"
                      defaultMessage="Use the roller to zoom into the mouse location"
                    />
                  )}
                </p>
              )}
              <Button
                variant=""
                className={`${styles.zoomInfoButton} p-0 text-decoration-link`}
                onClick={toggleCollapseZoomInfo}
              >
                {!collapseZoomInfo ? (
                  <FormattedMessage id="GLOBAL.HIDE" defaultMessage="HIDE" />
                ) : (
                  <FormattedMessage id="GLOBAL.SHOW" defaultMessage="SHOW" />
                )}
              </Button>
            </div>
          )}

          <span className="mx-sm"></span>
          {!isMobile && (
            <Zoom
              value={zoomLevel}
              disabled={loading || !selectedEcg}
              min={minZoom}
              max={maxZoom}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
            />
          )}
          <span className="mx-sm"></span>
          <div className="d-flex align-items-center">
            {displayFirstEcg ? (
              <FirstEcgButton />
            ) : (
              <CompareEcgs
                ecgs={comparableEcgs}
                selected={compareEcg?.id}
                onChange={compareEcgChangeHandler}
                disabled={compareEcgsDisabled || !selectedEcg || loading}
              />
            )}
          </div>
          {!isMobile && (
            <>
              <span className="mx-sm"></span>
              <Button
                variant=""
                className={`${styles.button} p-0`}
                onClick={handleShowEcgModal}
                disabled={!selectedEcg}
              >
                <img
                  src={enlargeEnableIcon}
                  alt=""
                  className={styles.buttonIcon}
                />
              </Button>
            </>
          )}
          {/*TODO: Change Icon*/}
          {isMobile && (
            <>
              <span className="mx-sm"></span>
              <Button
                variant=""
                className={`${styles.button} p-0`}
                onClick={onShowEcgMeta}
                disabled={!selectedEcg}
              >
                <img
                  src={ecgMetaModalIcon}
                  alt=""
                  className={styles.buttonIcon}
                />
              </Button>
            </>
          )}
        </div>
      </header>

      <EcgGraphModal
        show={graphModalOpen && !!selectedEcg}
        date={selectedEcg?.date}
        graph={selectedEcg?.graph}
        onClose={handleCloseEcgModal}
      />

      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};
export default EcgDetailsTopBar;
